import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Container,
    useMediaQuery,
    useTheme,
    Button,
    CircularProgress,
    Chip,
} from '@mui/material';
import CustomAppBar from "../../components/CustomAppBar";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatAssessmentText, getAssessmentText, hasData, getChipStatus } from '../../lib/formatting';
import { cardStyle } from "../../lib/uiformatting";
import { callBackendApiGet, alertOps } from "../../lib/api";
import { updatePortalData } from '../../redux/portalSlice';
import { useAuth0 } from "@auth0/auth0-react";
import { setEmail, setPhoneNo } from '../../redux/userSlice';
import logDrtEvent from '../../lib/logging';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CheckCircle, Calendar, User, Users, FileText, Clock } from 'lucide-react';

dayjs.extend(utc);
dayjs.extend(timezone);

const CaseDetails = () => {
    const [value, setValue] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const didFetchRef = React.useRef(false);
    const user_redux = useSelector((state) => state.user); //using for alert ops since the auth user doesn't have a phone number
    const { caseID } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();

    const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0();

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const COLLAPSED_HEIGHT = isDesktop ? 320 : 220;

    const { mostRecentCase, allCases, patients } = useSelector((state) => state.portal);

    const viaBottomNav = location.state?.viaBottomNav === true;
    const viaCaseList = location.state?.viaCaseList === true;
    const arrivedViaInAppNav =
        (viaBottomNav || viaCaseList) && (navigationType === 'PUSH' || navigationType === 'REPLACE');
    const initialSkipRef = React.useRef(arrivedViaInAppNav);

    React.useEffect(() => {
        if (viaBottomNav || viaCaseList) {
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, [viaBottomNav, viaCaseList, navigate, location.pathname, location.search]);

    React.useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) return;
        if (!user?.phone_number) return;

        if (initialSkipRef.current) {
            initialSkipRef.current = false;
            didFetchRef.current = true;
            setLoading(false);
            return;
        }

        if (didFetchRef.current) return;
        didFetchRef.current = true;

        (async () => {
            try {
                setLoading(true);
                const token = await getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
                });
                const res = await callBackendApiGet(
                    `/api/v1/portal/patient?phone=${encodeURIComponent(user.phone_number)}`,
                    {},
                    token
                );
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const data = await res.json();

                if (Array.isArray(data) && data.length > 0) {
                    dispatch(updatePortalData({ patientData: data }));
                    const patient = data[0];
                    if (patient) {
                        if (patient?.contact?.email) dispatch(setEmail(patient?.contact?.email));
                        if (patient?.contact?.phone) dispatch(setPhoneNo(patient?.contact?.phone));
                    }
                }
            } catch (err) {
                await alertOps(user_redux, "Portal information", "Error while fetching portal information for patients with phone: " + err, "fetchPortalInfo");
                logDrtEvent(`Error while fetching portal information for patients with phone: ${err}`);
            } finally {
                setLoading(false);
            }
        })();
    }, [isLoading, isAuthenticated, user, dispatch, navigate, getAccessTokenSilently]);

    const currentCase = React.useMemo(() => {
        if (!caseID) return undefined;
        if (mostRecentCase?.id && caseID === mostRecentCase.id) return mostRecentCase;
        return allCases?.find((c) => c.id === caseID);
    }, [caseID, mostRecentCase, allCases]);

    if (isLoading || loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.background.default }}>
                <CircularProgress color="secondary" />
            </Box>
        );
    }

    if (!currentCase) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.background.default }}>
                <Typography>Case not found</Typography>
            </Box>
        );
    }

    const showData = hasData(currentCase);
    const assessmentText = getAssessmentText(currentCase);
    const dermPlan = currentCase?.plan || "";
    const npPlan = currentCase?.np_plan || "";
    const isComplete = getChipStatus(currentCase);
    const patient = currentCase ? patients?.[currentCase.patientID] : null;

    const containerStyle = {
        maxWidth: isDesktop ? '1000px' : 'sm',
        pt: isDesktop ? 4 : 2,
        pb: isDesktop ? 4 : 2,
        px: isDesktop ? 4 : 4
    };

    const renderPlanLines = (plan) =>
        (plan || "No plan available")
            .split('\n')
            .map((line, idx) => (
                <Typography
                    key={idx}
                    component="span"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        fontSize: '0.9rem',
                        fontWeight: 400,
                        color: '#374151',
                        lineHeight: 1.6,
                        display: 'block',
                        mb: line.trim() === '' ? 1 : 0.5,
                    }}
                >
                    {line}
                </Typography >
            ));

    const renderPrescribedMedications = () => {
        const meds = currentCase.prescribed_medications;
        if (!meds || (Array.isArray(meds) && meds.length === 0))
            return (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                    No medications prescribed.
                </Typography>
            );

        if (typeof meds === 'string')
            return (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                    {meds}
                </Typography>
            );

        if (Array.isArray(meds)) {
            return meds.map((med, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                        <strong style={{ color: '#111827', fontWeight: 600 }}>{med.treatment.name}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                        How to take: {med.instructions || 'No instructions provided'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                        Duration: Use for {med.daysSupply ? `${med.daysSupply} ${med.daysSupply > 1 ? 'days' : 'day'}` : 'Duration not specified'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                        Refills available: Your care team will refill your prescription as needed.
                    </Typography>
                </Box>
            ));
        }
        return (
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>
                Invalid prescribed medications format
            </Typography>
        );
    };

    const AddendumNotesCard = ({ addendum }) => {
        const [expanded, setExpanded] = React.useState(false);
        const [overflowing, setOverflowing] = React.useState(false);
        const contentRef = React.useRef(null);

        // Combine all addendum text for display
        const allAddendumText = addendum.map((item) => item?.addendum_text ?? '—').join('\n\n');

        React.useEffect(() => {
            const el = contentRef.current;
            if (!el) return;
            const id = requestAnimationFrame(() => {
                setOverflowing(el.scrollHeight > COLLAPSED_HEIGHT + 1);
            });
            return () => cancelAnimationFrame(id);
        }, [allAddendumText, COLLAPSED_HEIGHT, isDesktop]);

        return (
            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #f3f4f6',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                    pt: 2,
                    px: 2,
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontWeight: 700,
                            fontSize: '1.125rem',
                            color: '#1a1a1a',
                            mb: 1.5,
                        }}
                    >
                        Updates to Your Plan
                    </Typography>
                    <Box
                        ref={contentRef}
                        sx={{
                            maxHeight: expanded ? 'none' : `${COLLAPSED_HEIGHT}px`,
                            overflow: expanded ? 'visible' : 'hidden',
                            position: 'relative',
                        }}
                    >
                        <Typography
                            component="div"
                            sx={{
                                fontSize: '0.9rem',
                                fontWeight: 400,
                                color: '#374151',
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {allAddendumText}
                        </Typography>
                        {!expanded && overflowing && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: 48,
                                    background: `linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))`,
                                }}
                            />
                        )}
                    </Box>
                    {overflowing && (
                        <Box sx={{ mt: 1.5 }}>
                            <Button
                                variant="text"
                                onClick={() => setExpanded((p) => !p)}
                                startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{
                                    color: '#0F3D5E',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                    '& .MuiButton-startIcon': {
                                        color: '#0F3D5E',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'none',
                                    },
                                }}
                            >
                                {expanded ? 'Read Less' : 'Read Full Clinical Note'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderAddendumNotes = () => {
        const addendum = currentCase?.addendum;
        if (!Array.isArray(addendum) || addendum.length === 0) return null;

        return <AddendumNotesCard addendum={addendum} />;
    };


    const ExpandablePlanCard = ({ title, text }) => {
        const [expanded, setExpanded] = React.useState(false);
        const [overflowing, setOverflowing] = React.useState(false);
        const contentRef = React.useRef(null);

        React.useEffect(() => {
            const el = contentRef.current;
            if (!el) return;
            const id = requestAnimationFrame(() => {
                setOverflowing(el.scrollHeight > COLLAPSED_HEIGHT + 1);
            });
            return () => cancelAnimationFrame(id);
        }, [text, COLLAPSED_HEIGHT, isDesktop]);

        if (!text) return null;

        return (
            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #f3f4f6',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                    pt: 2,
                    px: 2,
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontWeight: 700,
                            fontSize: '1.125rem',
                            color: '#1a1a1a',
                            mb: 1.5,
                        }}
                    >
                        {title}
                    </Typography>
                    <Box
                        ref={contentRef}
                        sx={{
                            maxHeight: expanded ? 'none' : `${COLLAPSED_HEIGHT}px`,
                            overflow: expanded ? 'visible' : 'hidden',
                            position: 'relative',
                        }}
                    >
                        {renderPlanLines(text)}
                        {!expanded && overflowing && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: 48,
                                    background: `linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))`,
                                }}
                            />
                        )}
                    </Box>
                    {overflowing && (
                        <Box sx={{ mt: 1.5 }}>
                            <Button
                                variant="text"
                                onClick={() => setExpanded((p) => !p)}
                                startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{
                                    color: '#0F3D5E',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                    '& .MuiButton-startIcon': {
                                        color: '#0F3D5E',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'none',
                                    },
                                }}
                            >
                                {expanded ? 'Read Less' : 'Read Full Clinical Note'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const handleBack = () => {
        navigate('/case-list');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pb: 7, bgcolor: theme.palette.background.default }}>
            <CustomAppBar noBack={false} returning={false} portal={true} onBack={handleBack} />

            <Container sx={{ ...containerStyle, flex: 1 }}>
                {!isComplete ? (
                    <>
                        {/* In Progress View */}
                        {/* Header Section */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                            <Box
                                sx={{
                                    backgroundColor: '#fff7ed',
                                    borderRadius: '8px',
                                    p: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <FileText size={24} color="#9a3412" />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1a1a1a',
                                        fontSize: '1.25rem',
                                        mb: 1,
                                    }}
                                >
                                    {currentCase.patient_reported_concern} {currentCase.is_returning ? 'Follow-Up' : 'Visit'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Chip
                                        icon={<Clock size={14} color="#9a3412" />}
                                        label="In Progress"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#fff7ed',
                                            color: '#9a3412',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            borderRadius: '16px',
                                            border: '1px solid #ffedd5',
                                            '& .MuiChip-icon': {
                                                color: '#9a3412',
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Patient Card */}
                        {patient && (
                            <Card
                                elevation={0}
                                sx={{
                                    mb: 3,
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                        <User size={18} color="#8b8b8b" style={{ flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                            Patient: <span style={{ color: '#111827', fontWeight: 600 }}>{patient.full_name || `${patient.first_name} ${patient.last_name}`}</span>
                                        </Typography>
                                    </Box>

                                    {currentCase.submitted_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Calendar size={18} color="#8b8b8b" style={{ flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                                Date: <span style={{ color: '#111827', fontWeight: 600 }}>
                                                    {new Date(currentCase.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </span>
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Current Status Card */}
                        <Card
                            elevation={0}
                            sx={{
                                mb: 3,
                                background: 'linear-gradient(to bottom right, rgba(255, 247, 237, 0.8), rgba(253, 242, 248, 0.6))',
                                borderRadius: '12px',
                                border: '1px solid #fed7aa',
                                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                                p: 1.5,
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            backgroundColor: 'white',
                                            borderRadius: '50%',
                                            p: 0.75,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            border: '2px solid #fb923c',
                                        }}
                                    >
                                        <Clock size={14} color="#fb923c" />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#1a1a1a',
                                                fontSize: '0.95rem',
                                                mb: 0.5,
                                            }}
                                        >
                                            Current Status
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#374151',
                                                fontSize: '0.875rem',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Your provider is reviewing your case. You'll receive a response within 48 hours with your diagnosis and treatment plan.
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* What You Submitted Section */}
                        <Card
                            elevation={0}
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                border: '1px solid #f3f4f6',
                                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                                p: 2,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: '#111827',
                                    fontSize: '1rem',
                                    mb: 2,
                                }}
                            >
                                What You Submitted
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Intake Form Item */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: '8px',
                                        background: 'linear-gradient(to bottom right, rgba(240, 253, 250, 0.4), rgba(236, 253, 245, 0.3))',
                                        border: '1px solid #ccfbf1',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '8px',
                                            backgroundColor: '#ccfbf1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FileText size={16} color="#0f766e" />
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: 1,
                                            fontWeight: 500,
                                            color: '#111827',
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        Intake form
                                    </Typography>
                                    <CheckCircle size={16} color="#0d9488" style={{ flexShrink: 0 }} />
                                </Box>
                            </Box>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Completed View - Existing Code */}
                        {/* Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                    <Box
                        sx={{
                            backgroundColor: '#ccfbf1',
                            borderRadius: '50%',
                            p: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <FileText size={24} color="#0d9488" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1a1a1a',
                                fontSize: '1.25rem',
                                mb: 1,
                            }}
                        >
                            {currentCase.patient_reported_concern} {currentCase.is_returning ? 'Follow-Up' : 'Visit'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {isComplete && (
                                <Chip
                                    icon={<CheckCircle size={14} color="#0d9488" />}
                                    label="Complete"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#ccfbf1',
                                        color: '#0d9488',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        borderRadius: '16px',
                                        '& .MuiChip-icon': {
                                            color: '#0d9488',
                                        },
                                    }}
                                />
                            )}
                            {currentCase.is_returning && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    Follow-up Visit
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Details Card */}
                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        {/* Patient */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <User size={18} color="#8b8b8b" style={{ flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                Patient: <span style={{ color: '#111827', fontWeight: 600 }}>{currentCase.patient_name}</span>
                            </Typography>
                        </Box>

                        {/* Date */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Calendar size={18} color="#8b8b8b" style={{ flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                Date: <span style={{ color: '#111827', fontWeight: 600 }}>
                                    {new Date(currentCase.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            </Typography>
                        </Box>

                        {/* Provider */}
                        {(currentCase.md_submitted_by_name || currentCase.np_submitted_by_name) && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Users size={18} color="#8b8b8b" style={{ flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem'}}>
                                    Care team: <span style={{ color: '#111827', fontWeight: 600 }}>
                                        {currentCase.md_submitted_by_name && `Dr. ${currentCase.md_submitted_by_name}`}
                                        {currentCase.md_submitted_by_name && currentCase.np_submitted_by_name && ', '}
                                        {currentCase.np_submitted_by_name && `${currentCase.np_submitted_by_name}`}
                                    </span>
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {showData ? (
                    <>
                        <Card
                            elevation={0}
                            sx={{
                                mb: 3,
                                background: 'linear-gradient(to bottom right, rgba(255, 247, 237, 0.6), rgba(253, 242, 248, 0.4))',
                                borderRadius: '12px',
                                border: '1px solid #ffedd5',
                                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                                pt: 2,
                                px: 2,
                                pb: 0.5,
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '1.125rem',
                                        color: '#1a1a1a',
                                        mb: 1,
                                    }}
                                >
                                    Diagnosis
                                </Typography>
                                <Box
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 400,
                                        color: '#374151',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {formatAssessmentText(assessmentText)}
                                </Box>
                            </CardContent>
                        </Card>
                        <Card
                            elevation={0}
                            sx={{
                                mb: 3,
                                background: 'linear-gradient(to bottom right, rgba(240, 253, 250, 0.6), rgba(236, 253, 245, 0.4))',
                                borderRadius: '12px',
                                border: '1px solid #ccfbf1',
                                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                                pt: 2,
                                px: 2,
                                pb: 0.5,
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '1.125rem',
                                        color: '#1a1a1a',
                                        mb: 1,
                                    }}
                                >
                                    Treatment Plan
                                </Typography>
                                <Box
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        color: '#374151',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {renderPrescribedMedications()}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Derm plan */}
                        <ExpandablePlanCard title="Dermatologist's Plan" text={dermPlan} />

                        {/* NP plan */}
                        <ExpandablePlanCard title="Nurse Practitioner's Plan" text={npPlan} />

                        {/* Addendum notes (only if present) */}
                        {renderAddendumNotes()}

                    </>
                ) : (
                    <Card sx={{ ...cardStyle, mx: isDesktop ? 'auto' : 2 }} elevation={0}>
                        <CardContent>
                            <Typography variant="body1" align="center">
                                Your case is under review. Your care team will update the notes in 24-48hrs.
                            </Typography>
                        </CardContent>
                    </Card>
                )}
                    </>
                )}
            </Container>
        </Box>
    );
};

export default CaseDetails;
